import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useOverlayContext } from '@providers/OverlayProvider';
import OVERLAYS from '@constants/overlays';

import type { AiTreeSection } from './AiScripts/kind';
import {
  EditorFocus,
  EditorNodeKind,
  EditorSelectedNode,
} from './editorCategories';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface EditorModeContextValue {
  selectedNode: EditorSelectedNode;
  selectMapAndColors: () => void;
  selectSpot: (ueIdentifier: string) => void;
  selectRegionScript: (trigger: string, scriptId: string | null) => void;
  clearSelection: () => void;

  activeFocus: EditorFocus;
  setActiveFocus: (focus: EditorFocus) => void;

  // Which section of the assistant (AI Scripts) focus the left panel shows:
  // 'scripts' (AI triggers + spots, unified) or 'knowledge'. Lives here (not
  // local to the tree) because the UE selection bridges flip it on spot clicks
  // and on entering a live AI trigger.
  aiTreeSection: AiTreeSection;
  setAiTreeSection: (section: AiTreeSection) => void;

  // When true, walking into an AI trigger (SYNC_DATA_UE.audioTrigger.aiTriggerId)
  // auto-opens the inspector for that trigger instead of letting the virtual
  // assistant (Noa) play. Editor-only convenience; default off.
  followLiveTrigger: boolean;
  setFollowLiveTrigger: (value: boolean) => void;

  isSceneTreeCollapsed: boolean;
  toggleSceneTree: () => void;

  isQuickSwitcherOpen: boolean;
  openQuickSwitcher: () => void;
  closeQuickSwitcher: () => void;

  autosaveStatus: AutosaveStatus;
  setAutosaveStatus: (status: AutosaveStatus) => void;

  // Recent in-world spot clicks (UE → hub via CUSTOMISATION_ITEM_SELECTED).
  // Used by the AI Scripts create popover to suggest regions the user has
  // recently interacted with. Capped + deduped; reset between editor sessions.
  recentSceneClicks: string[];
  registerSceneClick: (id: string) => void;
}

const EditorModeContext = createContext<EditorModeContextValue | null>(null);

interface EditorModeProviderProps {
  children: ReactNode;
  initialFocus?: EditorFocus;
}

const getDefaultSelectionForFocus = (
  focus: EditorFocus,
): EditorSelectedNode => {
  if (focus === 'environment') {
    return { kind: EditorNodeKind.MAP_AND_COLORS };
  }
  return null;
};

export const EditorModeProvider = ({
  children,
  initialFocus = 'environment',
}: EditorModeProviderProps) => {
  const [activeFocus, setActiveFocusState] =
    useState<EditorFocus>(initialFocus);
  const [aiTreeSection, setAiTreeSection] =
    useState<AiTreeSection>('scripts');
  const [followLiveTrigger, setFollowLiveTrigger] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<EditorSelectedNode>(() =>
    getDefaultSelectionForFocus(initialFocus),
  );
  const [isSceneTreeCollapsed, setSceneTreeCollapsed] = useState(false);
  const [isQuickSwitcherOpen, setQuickSwitcherOpen] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [recentSceneClicks, setRecentSceneClicks] = useState<string[]>([]);

  const overlay = useOverlayContext();

  const closeExclusiveOverlays = useCallback(() => {
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.ADMIN_TOOLS);
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.PROFILE);
  }, [overlay]);

  const registerSceneClick = useCallback((id: string) => {
    if (!id) {
      return;
    }
    setRecentSceneClicks((prev) => {
      const trimmed = id.trim();
      const filtered = prev.filter((existing) => existing !== trimmed);
      // Newest first, capped to keep the create popover from growing unbounded.
      return [trimmed, ...filtered].slice(0, 8);
    });
  }, []);

  const selectMapAndColors = useCallback(() => {
    closeExclusiveOverlays();
    setSelectedNode({ kind: EditorNodeKind.MAP_AND_COLORS });
  }, [closeExclusiveOverlays]);

  const selectSpot = useCallback(
    (ueIdentifier: string) => {
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.SPOT, ueIdentifier });
    },
    [closeExclusiveOverlays],
  );

  const selectRegionScript = useCallback(
    (trigger: string, scriptId: string | null) => {
      closeExclusiveOverlays();
      setSelectedNode({
        kind: EditorNodeKind.REGION_SCRIPT,
        trigger,
        scriptId,
      });
    },
    [closeExclusiveOverlays],
  );

  const clearSelection = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const setActiveFocus = useCallback((focus: EditorFocus) => {
    setActiveFocusState(focus);
    setSelectedNode(getDefaultSelectionForFocus(focus));
  }, []);

  const toggleSceneTree = useCallback(() => {
    setSceneTreeCollapsed((prev) => !prev);
  }, []);

  const openQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(true);
  }, []);

  const closeQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(false);
  }, []);

  const value = useMemo<EditorModeContextValue>(
    () => ({
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectRegionScript,
      clearSelection,
      activeFocus,
      setActiveFocus,
      aiTreeSection,
      setAiTreeSection,
      followLiveTrigger,
      setFollowLiveTrigger,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      setAutosaveStatus,
      recentSceneClicks,
      registerSceneClick,
    }),
    [
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectRegionScript,
      clearSelection,
      activeFocus,
      setActiveFocus,
      aiTreeSection,
      followLiveTrigger,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      recentSceneClicks,
      registerSceneClick,
    ],
  );

  return (
    <EditorModeContext.Provider value={value}>
      {children}
    </EditorModeContext.Provider>
  );
};

export const useEditorMode = (): EditorModeContextValue => {
  const ctx = useContext(EditorModeContext);
  if (!ctx) {
    throw new Error('useEditorMode must be used inside EditorModeProvider');
  }
  return ctx;
};
